import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { User, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, BadgeCheck, Headphones } from "lucide-react";
import { toast } from "sonner";

const REMEMBER_KEY = "cb_remember_id";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) { setIdentifier(saved); setRemember(true); }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      if (remember) localStorage.setItem(REMEMBER_KEY, identifier.trim());
      else localStorage.removeItem(REMEMBER_KEY);
      toast.success("Bem-vindo de volta!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgotPassword = () => navigate("/forgot-password");

  const trust = [
    { icon: ShieldCheck, label: "Atendimento rápido e seguro" },
    { icon: BadgeCheck, label: "Profissionais qualificados" },
    { icon: Headphones, label: "Suporte sempre que precisar" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#eef4ff] to-slate-100 flex justify-center">
      <div className="w-full max-w-md flex flex-col">
        {/* Hero da marca */}
        <div className="relative overflow-hidden rounded-b-[2.5rem] bg-gradient-to-br from-[#0a2a6c] via-[#0f3d92] to-[#1e7be0] text-white px-6 pt-8 pb-16 shadow-lg">
          <div className="flex items-center gap-3">
            <img src="/logo-clima-branca-icon.png" alt="Clima Branca"
              className="h-12 w-12 rounded-xl object-cover ring-2 ring-white/30 bg-white/10" />
            <div className="leading-tight">
              <span className="text-lg font-bold block">Clima Branca</span>
              <span className="text-[11px] font-semibold tracking-widest text-white/70 uppercase">
                Soluções em climatização
              </span>
            </div>
          </div>

          <div className="mt-6 flex items-end gap-3">
            <div className="flex-1">
              <h2 className="text-xl font-bold leading-snug">
                Conforto e confiança em cada atendimento.
              </h2>
            </div>
            <img src="/login-tech.jpg" alt="Técnico instalando ar-condicionado"
              className="h-28 w-28 rounded-2xl object-cover ring-2 ring-white/25 shadow-xl flex-shrink-0" />
          </div>

          {/* onda inferior */}
          <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 500 40" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0,20 C150,50 350,-10 500,20 L500,40 L0,40 Z" fill="#eef4ff" />
          </svg>
        </div>

        {/* Card do formulário */}
        <div className="px-5 -mt-6">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6">
            <h1 className="text-2xl font-bold text-slate-800">Bem-vindo de volta!</h1>
            <p className="text-sm text-slate-500 mt-1 mb-5">Faça login para acessar sua conta</p>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="identifier" className="text-sm font-medium text-slate-600">E-mail ou CPF/CNPJ</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="identifier" type="text" required value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="seu@email.com ou 000.000.000-00"
                    className="pl-9 h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="login-email-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="password" className="text-sm font-medium text-slate-600">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="password" type={showPwd ? "text" : "password"} required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-9 pr-10 h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    data-testid="toggle-password-visibility">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me-checkbox" />
                  Lembrar meus dados
                </label>
                <button type="button" onClick={forgotPassword}
                  className="text-sm text-blue-600 font-medium hover:underline" data-testid="forgot-password-link">
                  Esqueci minha senha
                </button>
              </div>

              <Button type="submit" disabled={loading}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1e7be0] to-[#0f3d92] hover:from-[#1a6fce] hover:to-[#0c3382] gap-2 text-base font-semibold shadow-md"
                data-testid="login-submit-button">
                {loading ? "Entrando..." : <>Entrar <ArrowRight className="h-5 w-5" /></>}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="h-px bg-slate-200 flex-1" />
              <span className="text-xs text-slate-400">ou continue com</span>
              <div className="h-px bg-slate-200 flex-1" />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Button type="button" variant="outline" className="h-11 rounded-xl gap-2 border-slate-200"
                onClick={googleLogin} data-testid="google-login-button">
                <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />
                Continuar com Google
              </Button>
            </div>

            <p className="text-sm text-slate-500 text-center mt-6">
              Ainda não tem uma conta?{" "}
              <Link to="/register" className="text-blue-600 font-semibold hover:underline" data-testid="go-to-register-link">
                Criar conta
              </Link>
            </p>
            <p className="text-sm text-slate-500 text-center mt-2">
              É técnico?{" "}
              <Link to="/credenciamento" className="text-emerald-700 font-medium hover:underline" data-testid="go-to-credenciamento-link">
                Seja um técnico credenciado
              </Link>
            </p>
          </div>
        </div>

        {/* Barra de confiança */}
        <div className="grid grid-cols-3 gap-2 px-5 mt-6">
          {trust.map((t) => (
            <div key={t.label} className="flex flex-col items-center text-center gap-1.5">
              <span className="h-10 w-10 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center">
                <t.icon className="h-5 w-5 text-[#1e7be0]" />
              </span>
              <span className="text-[11px] leading-tight text-slate-600">{t.label}</span>
            </div>
          ))}
        </div>

        {/* Rodapé */}
        <div className="mt-auto relative pt-10">
          <svg className="absolute top-2 left-0 w-full" viewBox="0 0 500 40" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0,20 C150,-10 350,50 500,20 L500,40 L0,40 Z" fill="#0a2a6c" />
          </svg>
          <div className="bg-[#0a2a6c] text-white/80 text-center py-5 px-5">
            <div className="flex items-center justify-center gap-2">
              <img src="/logo-clima-branca-icon.png" alt="Clima Branca" className="h-6 w-6 rounded-md object-cover" />
              <span className="text-sm font-semibold text-white">Clima Branca</span>
            </div>
            <p className="text-xs text-white/60 mt-1.5">© {new Date().getFullYear()} Clima Branca · Soluções em climatização</p>
          </div>
        </div>
      </div>
    </div>
  );
}
